/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid ui/pagination.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import { interpolate } from '../labels'
import type { DataGridLabels, DataGridPaginationInfoState } from '../types'

const CLASS_NAME_PAGINATION = 'data-grid-pagination'
const CLASS_NAME_PAGINATION_INFO = 'data-grid-pagination-info'

const PAGE_WINDOW = 5

export interface GridPaginationContext {
  info: boolean | ((state: DataGridPaginationInfoState) => string)
  labels: DataGridLabels
  pageSizeOptions: number[]
}

export interface GridPagination {
  element: HTMLDivElement
  update: (state: DataGridPaginationInfoState) => void
}

const createPageItem = (
  label: string,
  page: string,
  options: { active?: boolean; disabled?: boolean; ariaLabel?: string } = {}
): HTMLLIElement => {
  const item = document.createElement('li')
  item.classList.add('page-item')
  if (options.active) {
    item.classList.add('active')
    item.setAttribute('aria-current', 'page')
  }

  if (options.disabled) {
    item.classList.add('disabled')
  }

  const button = document.createElement('button')
  button.type = 'button'
  button.classList.add('page-link')
  button.textContent = label
  button.disabled = Boolean(options.disabled)
  button.dataset.coreuiPage = page
  if (options.ariaLabel) {
    button.setAttribute('aria-label', options.ariaLabel)
  }

  item.append(button)

  return item
}

export const createPagination = (context: GridPaginationContext): GridPagination => {
  const element = document.createElement('div')
  element.classList.add(CLASS_NAME_PAGINATION)

  const nav = document.createElement('nav')
  nav.setAttribute('aria-label', context.labels.paginationLabel)
  const list = document.createElement('ul')
  list.classList.add('pagination', 'pagination-sm')
  nav.append(list)

  const info = context.info === false ? null : document.createElement('span')
  if (info) {
    info.classList.add(CLASS_NAME_PAGINATION_INFO)
  }

  const select = document.createElement('select')
  select.classList.add('form-select', 'form-select-sm')
  select.setAttribute('aria-label', context.labels.pageSizeLabel)
  select.dataset.coreuiPageSize = ''
  for (const size of context.pageSizeOptions) {
    const option = document.createElement('option')
    option.value = String(size)
    option.textContent = String(size)
    select.append(option)
  }

  element.append(nav, ...(info ? [info] : []), select)

  return {
    element,
    update(state) {
      const lastPageIndex = Math.max(state.pageCount - 1, 0)
      const windowStart = Math.max(
        Math.min(state.pageIndex - Math.floor(PAGE_WINDOW / 2), state.pageCount - PAGE_WINDOW),
        0
      )
      // No pages (server mode before the first load, rejected provider) - no
      // page buttons, only the disabled first/prev/next/last chrome.
      const windowEnd = state.pageCount === 0 ? -1 : Math.min(windowStart + PAGE_WINDOW - 1, lastPageIndex)

      const items = [
        createPageItem('«', 'first', {
          ariaLabel: context.labels.firstPage,
          disabled: state.pageIndex === 0
        }),
        createPageItem('‹', 'prev', {
          ariaLabel: context.labels.previousPage,
          disabled: state.pageIndex === 0
        })
      ]
      for (let page = windowStart; page <= windowEnd; page++) {
        items.push(createPageItem(String(page + 1), String(page), { active: page === state.pageIndex }))
      }

      items.push(
        createPageItem('›', 'next', {
          ariaLabel: context.labels.nextPage,
          disabled: state.pageIndex >= lastPageIndex
        }),
        createPageItem('»', 'last', {
          ariaLabel: context.labels.lastPage,
          disabled: state.pageIndex >= lastPageIndex
        })
      )
      list.replaceChildren(...items)

      if (info) {
        if (typeof context.info === 'function') {
          info.textContent = context.info(state)
        } else {
          const first = state.totalRows === 0 ? 0 : (state.pageIndex * state.pageSize) + 1
          const last = Math.min((state.pageIndex + 1) * state.pageSize, state.totalRows)
          info.textContent = interpolate(context.labels.itemsInfo, {
            first,
            last,
            total: state.totalRows
          })
        }
      }

      // A configured pageSize outside pageSizeOptions would leave the select
      // blank; surface it as an extra option in numeric position.
      if (![...select.options].some(option => option.value === String(state.pageSize))) {
        const option = document.createElement('option')
        option.value = String(state.pageSize)
        option.textContent = String(state.pageSize)
        select.insertBefore(option, [...select.options].find(item => Number(item.value) > state.pageSize) ?? null)
      }

      select.value = String(state.pageSize)
    }
  }
}
